import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { switchProp } from 'styled-tools';
import { breakpoints } from '~/theme/breakpoints';
import { ResetButton } from '../Button/ResetButton';

type ModalTypes = 'gallery' | 'search' | 'searchMobile';

export const Wrapper = styled(motion.div)<{ variant?: ModalTypes; marginTop?: number }>(
    ({ theme }) => ({
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndices.max,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }),
    switchProp('variant', {
        search: ({ marginTop }) => ({
            top: `calc(${marginTop}px - 1px)`,
            height: 'auto',
            zIndex: 199,
            backgroundColor: 'rgba(0, 0, 0, 0.5);',
            [breakpoints.sm]: {
                height: '100vh', // Fallback for browsers without svh support
                '@media (min-height: 1svh)': {
                    height: '100svh',
                },
            },
        }),
        searchMobile: ({ marginTop }) => ({
            top: marginTop,
            bottom: 0,
            height: `calc(100vh - ${marginTop}px)`, // Fallback for browsers without svh support
            zIndex: 201, // Higher than main menu header (200)
            backgroundColor: 'rgba(0, 0, 0, 0.5);',
            // Use svh for better iOS keyboard handling
            '@media (min-height: 1svh)': {
                height: `calc(100svh - ${marginTop}px)`,
            },
            [breakpoints.sm]: {
                height: `calc(100vh - ${marginTop}px)`, // Fallback
                '@media (min-height: 1svh)': {
                    height: `calc(100svh - ${marginTop}px)`,
                },
            },
        }),
    }),
);

export const Main = styled(motion.div)<{ variant?: ModalTypes }>(
    ({ theme }) => ({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.colors.white,
    }),
    switchProp('variant', {
        gallery: ({ theme }) => ({
            backgroundColor: theme.colors.productImageBackround,
            img: {
                mixBlendMode: 'multiply',
            },
        }),
        search: () => ({
            height: 'auto',
            width: '100%',
            padding: '10px 0 0 0',
            display: 'flex',
            flexDirection: 'column',
            [breakpoints.sm]: {
                height: 'fit-content',
            },
        }),
        searchMobile: () => ({
            height: '100%',
            margin: '0 auto',
            maxWidth: '1480px',
            width: '100%',
            padding: '10px 0 0 0',
            display: 'flex',
            flexDirection: 'column',
            // Prevent iOS keyboard from triggering scroll events
            position: 'relative',
            [breakpoints.sm]: {
                height: '100%',
                maxHeight: 'none',
            },
        }),
    }),
);

export const Header = styled.div({
    position: 'relative',
});

export const Close = styled(ResetButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.space[2],
    right: theme.space[2],
    display: 'flex',
    padding: theme.space[3],
    borderRadius: '50%',
    background: theme.colors.black,
    zIndex: theme.zIndices.max,
}));

export const Content = styled.div<{ variant?: ModalTypes }>(
    () => ({
        height: 'calc(100% - 30px)',
    }),

    switchProp('variant', {
        gallery: ({ theme }) => ({
            backgroundColor: theme.colors.productImageBackround,
            height: 'calc(100%)',
            img: {
                mixBlendMode: 'multiply',
            },
        }),
        search: () => ({
            padding: '0 15px',
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            height: 'calc(100% - 30px)',
            overflowY: 'auto',
            [breakpoints.sm]: {
                height: 'fit-content',
                maxHeight: '632px',
                justifyContent: 'space-between',
                padding: '24px 32px 32px 32px',
            },
        }),
        searchMobile: () => ({
            padding: '0 15px',
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            height: 'calc(100% - 30px)',
            overflowY: 'auto',
            [breakpoints.sm]: {
                height: 'calc(100% - 30px)',
                maxHeight: 'none',
                justifyContent: 'space-between',
                padding: '24px 32px 32px 32px',
            },
        }),
    }),
);
